import { Checkbox, Text } from "north.js";
import React from "react";
import styled, { DefaultTheme } from "styled-components";

import { mediaQueries } from "src/theme/deviceBreakpoints";

import { formatTime, formatVolunteerShiftDate } from "../../shared/utils/date";
import { TRelativeTime, TRelativeTone } from "../constants";

interface ShiftRowProps {
  shiftName: string;
  startTime: Date;
  endTime: Date;
  relativeTime: TRelativeTime;
  checked: boolean;
  selectable: boolean;
  onToggle: () => void;
}

const ShiftRow: React.FC<ShiftRowProps> = ({
  shiftName,
  startTime,
  endTime,
  relativeTime,
  checked,
  selectable,
  onToggle,
}) => {
  const startDate = formatVolunteerShiftDate(startTime);
  const endDate = formatVolunteerShiftDate(endTime);

  const date = startDate === endDate ? startDate : `${startDate} - ${endDate}`;
  const time = `${formatTime(startTime)} - ${formatTime(endTime)}`;

  return (
    <Row $selectable={selectable} onClick={selectable ? onToggle : undefined}>
      <Checkbox
        onClick={(e) => e.stopPropagation()}
        onChange={onToggle}
        checked={checked}
        disabled={!selectable}
      />
      <Details>
        <Name type="body1">{shiftName}</Name>
        <DateColumn type="body2">{date}</DateColumn>
        <TimeColumn type="body2">{time}</TimeColumn>
        <RelativeColumn type="body2" $tone={relativeTime.tone}>
          {relativeTime.text}
        </RelativeColumn>
      </Details>
    </Row>
  );
};

const Row = styled.div<{ $selectable: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid
    ${({ theme }) => theme.globalConstants.color.backgroundGrayDark};
  cursor: ${({ $selectable }) => ($selectable ? "pointer" : "default")};
  opacity: ${({ $selectable }) => ($selectable ? 1 : 0.65)};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${({ theme, $selectable }) =>
      $selectable
        ? theme.globalConstants.color.backgroundLight
        : "transparent"};
  }
`;

const Name = styled(Text)`
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DateColumn = styled(Text)`
  font-weight: 400;
  white-space: nowrap;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

const TimeColumn = styled(Text)`
  font-weight: 700;
  white-space: nowrap;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
`;

type TColorKey = keyof DefaultTheme["globalConstants"]["color"];

const toneColors: Record<TRelativeTone, TColorKey> = {
  now: "textSuccess",
  soon: "textWarning",
  later: "textSecondary",
  past: "textSecondary",
};

const RelativeColumn = styled(Text)<{ $tone: TRelativeTone }>`
  font-weight: 700;
  white-space: nowrap;
  text-align: right;
  color: ${({ theme, $tone }) =>
    theme.globalConstants.color[toneColors[$tone]]};

  ${mediaQueries.tablet} {
    text-align: left;
  }
`;

const Details = styled.div`
  flex: 1;
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto 72px;
  align-items: center;
  column-gap: 20px;

  ${mediaQueries.tablet} {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    column-gap: 10px;
    row-gap: 2px;

    ${Name} {
      flex: 1 0 100%;
    }
  }
`;

export default ShiftRow;
